import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { NAV } from "../lib/nav";
import { useRoster, type RosterFilter } from "../lib/roster";
import { Copilot } from "./Copilot";
import { CommandPalette } from "./CommandPalette";
import { ViewHelp } from "./Help";
import { GuidePanel } from "./GuidePanel";
import { WelcomeTour } from "./WelcomeTour";
import { FieldsDock } from "./FieldsDock";
import { ErrorBoundary } from "./ErrorBoundary";
import { useIsPhone } from "../lib/useIsPhone";
import { c, font, pillBtn, tnum, withAlpha } from "../lib/theme";

// Every page the sidebar links to directly, so a sub-page match never
// double-highlights (e.g. /companies/potential is its own item).
const NAV_PATHS = new Set(NAV.flatMap((g) => g.items.map((it) => it.to)));

const NAV_COLLAPSE_KEY = "arcline_nav_collapsed";

function roleColor(role: string): string {
  return role === "admin" ? c.soft.coral : role === "partner" ? c.soft.accent2 : role === "analyst" ? c.soft.cyan : c.muted;
}

// "admin" → "Admin". The header used to shout the role in all-caps mono; the
// bento chrome states it plainly instead.
function titleCaseRole(role: string): string {
  return role ? role[0].toUpperCase() + role.slice(1) : "";
}

// Live session clock — a small ticking HH:MM:SS in the header, so the operating
// line always shows current time at a glance (matches the prototype shell).
function SessionClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  return (
    <span style={{ font: `600 13px ${font.sans}`, letterSpacing: "0.02em", color: c.muted2, whiteSpace: "nowrap" }}>
      <span style={{ color: c.green }}>●</span> {hh}:{mm}<span style={{ color: c.muted }}>:{ss}</span>
    </span>
  );
}

// A thin reminder that lists are filtered, so nobody wonders where half the
// companies went.
function RosterBanner() {
  const { roster, setRoster } = useRoster();
  const loc = useLocation();
  if (roster === "all" || loc.pathname.startsWith("/companies/")) return null;
  return (
    <div className="arc-banner" style={{ flex: "none", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, padding: "8px 28px", background: c.soft.accentDim, borderBottom: `1px solid ${c.line}`, font: `500 14px ${font.sans}`, color: c.textHi }}>
      <span>Showing <b>{roster === "onboarded" ? "Onboarded" : "Potential"}</b> companies only. Lists without a company (like opportunities) are not affected.</span>
      <button onClick={() => setRoster("all")} style={{ font: `600 13px ${font.sans}`, color: c.blueHi, background: "none", border: "none", textDecoration: "underline", cursor: "pointer" }}>Show all companies</button>
    </div>
  );
}

// Header roster filter — narrows every company list in the app to Potential
// or Onboarded companies (see lib/roster.tsx). On the two roster pages the
// page itself already decides, so the switch shows that and stands aside.
function RosterSwitch() {
  const { roster, setRoster, counts } = useRoster();
  const loc = useLocation();
  const locked = loc.pathname === "/companies/potential" ? "potential" : loc.pathname === "/companies/onboarded" ? "onboarded" : null;
  const current: RosterFilter = locked ?? roster;
  const opts: Array<{ v: RosterFilter; label: string }> = [
    { v: "all", label: "All" },
    { v: "potential", label: `Potential · ${counts.potential}` },
    { v: "onboarded", label: `Onboarded · ${counts.onboarded}` },
  ];
  return (
    <div role="group" aria-label="Show companies" title={locked ? "This page shows one roster; the switch applies on every other page" : "Show only Potential or Onboarded companies on every page"}
      style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ font: `600 13px ${font.sans}`, color: c.muted, whiteSpace: "nowrap" }}>Show</span>
      <div style={{ display: "flex", border: `1px solid ${c.line2}`, borderRadius: 6, overflow: "hidden", opacity: locked ? 0.6 : 1 }}>
        {opts.map((o, i) => {
          const on = current === o.v;
          return (
            <button key={o.v} type="button" disabled={Boolean(locked)} aria-pressed={on} onClick={() => setRoster(o.v)}
              style={{ font: `600 13px ${font.sans}`, padding: "6px 11px", border: "none", borderLeft: i ? `1px solid ${c.line2}` : "none", background: on ? c.blue : c.panel, color: on ? c.onAccent : c.muted2, cursor: locked ? "default" : "pointer", whiteSpace: "nowrap" }}>
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// On a phone the header has no room for the name, role, tour and sign-out
// buttons, so they fold into one account button with a small menu.
function AccountMenu({ name, roleLabel, roleTint, onTour, onLogout }: { name: string; roleLabel: string; roleTint: string; onTour?: () => void; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?";
  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label="Account menu"
        style={{ width: 38, height: 38, borderRadius: "50%", border: `1px solid ${c.line2}`, background: open ? c.blue : c.soft.card, color: open ? c.onAccent : c.textHi, font: `700 14px ${font.sans}`, cursor: "pointer" }}>
        {initials}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 94 }} />
          <div role="menu" style={{ position: "fixed", top: 60, right: 8, zIndex: 95, width: 250, maxWidth: "calc(100vw - 16px)", background: c.panel, border: `1px solid ${c.line2}`, borderRadius: 10, padding: 14, boxShadow: "0 18px 50px rgba(16,36,58,0.3)" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: c.textHi }}>{name}</div>
            <div style={{ font: `500 13px ${font.sans}`, color: roleTint, marginBottom: 12 }}>{roleLabel}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {onTour && <button role="menuitem" onClick={() => { setOpen(false); onTour(); }} style={pillBtn}>Tour</button>}
              <button role="menuitem" onClick={onLogout} style={pillBtn}>Sign out</button>
            </div>
          </div>
        </>
      )}
    </span>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const { me, logout } = useAuth();
  // A company user gets a short menu of their own company's pages; the
  // firm-wide pages, the Copilot and the roster switch are not theirs.
  const companyUser = me?.organization === "company";
  // Sidebar counts refresh once a minute and when the window regains focus,
  // not on every click (they read some of the heaviest lists in the app).
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const bump = () => setRefresh((n) => n + 1);
    const t = setInterval(bump, 60_000);
    window.addEventListener("focus", bump);
    window.addEventListener("arcline:refresh-sidebar", bump);
    return () => { clearInterval(t); window.removeEventListener("focus", bump); window.removeEventListener("arcline:refresh-sidebar", bump); };
  }, []);

  const loc = useLocation();
  const navigate = useNavigate();
  // Phones get a slide-out menu instead of the fixed sidebar. It closes when
  // a page is picked, on Escape, and when the screen grows back to desktop.
  const phone = useIsPhone();
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [loc.pathname, loc.search, phone]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);
  // Company users have only their own company's pages; anything else sends
  // them back to their company (the server refuses the data regardless).
  useEffect(() => {
    if (!companyUser) return;
    const ok = ["/companies", "/proposals", "/opportunities", "/reports", "/glossary"].some((p) => loc.pathname === p || loc.pathname.startsWith(`${p}/`));
    if (!ok && loc.pathname !== "/") navigate("/", { replace: true });
  }, [companyUser, loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  // 25+ links across 6 groups is a lot to scan — a quick filter narrows the
  // nav to matching groups/items instead of making people hunt for a page.
  const [navQuery, setNavQuery] = useState("");
  // Live pipeline card count for the nav badge. Refetched on navigation so a
  // drag-advance or new ingest is reflected without a manual reload.
  const [pipelineCount, setPipelineCount] = useState<number | null>(null);
  useEffect(() => {
    if (companyUser) return;
    let alive = true;
    api.pipeline().then((p) => { if (alive) setPipelineCount(p.totalCount); }).catch(() => {});
    return () => { alive = false; };
  }, [refresh, companyUser]);

  // "Which pages need input" — an amber cue on nav items needing setup (a feed
  // missing its key, an unset integration key). Refetched on navigation so the
  // cue clears as soon as the input is provided.
  const [needs, setNeeds] = useState<Record<string, string>>({});
  useEffect(() => {
    if (companyUser) return;
    let alive = true;
    api.setupStatus().then((s) => { if (alive) setNeeds(Object.fromEntries(s.items.map((it) => [it.route, it.reason]))); }).catch(() => {});
    return () => { alive = false; };
  }, [refresh, companyUser]);

  // Pinned companies — a quick-jump group at the top of the sidebar so leadership
  // can reach their priority deals without opening Companies and searching.
  // Refetched on navigation, same cadence as the two fetches above.
  const [pinnedCompanies, setPinnedCompanies] = useState<Array<{ id: number; name: string }>>([]);
  const [myCompanies, setMyCompanies] = useState<Array<{ id: number; name: string }>>([]);
  useEffect(() => {
    let alive = true;
    if (companyUser) {
      api.companies().then((cos) => { if (alive) setMyCompanies(cos.map((co) => ({ id: co.id, name: co.name })).sort((a, b) => a.name.localeCompare(b.name))); }).catch(() => {});
      return () => { alive = false; };
    }
    Promise.all([api.pinnedCompanies(), api.companies()]).then(([ids, companies]) => {
      if (!alive) return;
      const byId = new Map(companies.map((co) => [co.id, co.name]));
      setPinnedCompanies(ids.filter((id) => byId.has(id)).map((id) => ({ id, name: byId.get(id)! })));
    }).catch(() => {});
    return () => { alive = false; };
  }, [refresh, companyUser]);
  // Alerts badge — count of critical+high severity outstanding action items,
  // same aggregation and severity tiers ActionCenter.tsx already uses.
  // Refetched on navigation, same cadence as the fetches above.
  const [alertCount, setAlertCount] = useState(0);
  useEffect(() => {
    if (companyUser) return;
    let alive = true;
    api.actionItems().then((r) => {
      if (!alive) return;
      setAlertCount(r.items.filter((it) => it.severity === "critical" || it.severity === "high").length);
    }).catch(() => {});
    return () => { alive = false; };
  }, [refresh, companyUser]);

  // First-login welcome tour — fetched once per session, not per route, and
  // shown to every account that hasn't finished/skipped it yet (existing
  // accounts included, since the tour shipped after they signed up).
  const [showTour, setShowTour] = useState(false);
  useEffect(() => {
    let alive = true;
    if (me?.organization === "company") return;
    api.tourSeen().then((r) => { if (alive && !r.seen) setShowTour(true); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const finishTour = (): void => { setShowTour(false); api.setTourSeen().catch(() => {}); };

  // Collapsible nav groups — click a group title to fold it away. The choice is
  // remembered per browser, the group holding the current page always opens,
  // and a nav filter shows every match regardless of what's folded.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem(NAV_COLLAPSE_KEY) ?? "{}") as Record<string, boolean>; } catch { return {}; }
  });
  const saveCollapsed = (next: Record<string, boolean>): void => {
    setCollapsed(next);
    try { localStorage.setItem(NAV_COLLAPSE_KEY, JSON.stringify(next)); } catch { /* storage blocked — still works for this session */ }
  };
  const toggleGroup = (group: string): void => saveCollapsed({ ...collapsed, [group]: !collapsed[group] });
  const allCollapsed = NAV.every((g) => collapsed[g.group]);
  const setAll = (fold: boolean): void => saveCollapsed(Object.fromEntries(NAV.map((g) => [g.group, fold])));
  useEffect(() => {
    const active = NAV.find((g) => g.items.some((it) => it.to === loc.pathname));
    if (active && collapsed[active.group]) saveCollapsed({ ...collapsed, [active.group]: false });
    // Only re-open on navigation, not every time the user folds a group.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname]);

  const pinnedNav = pinnedCompanies.length > 0
    ? [{ group: "Pinned", subtitle: undefined as string | undefined, items: pinnedCompanies.map((p) => ({ to: `/companies?company=${p.id}`, label: p.name, glyph: "★", description: undefined as string | undefined })) }]
    : [];
  const companyNav = [
    { group: "My company", subtitle: "Your company's record, files and reports" as string | undefined, items: myCompanies.flatMap((co) => [
      { to: `/companies?company=${co.id}`, label: co.name, glyph: "▣", description: "Your company's page: profile, files, technical data, reports and proposals." as string | undefined },
      { to: `/companies/${co.id}/readiness`, label: myCompanies.length > 1 ? `${co.name}: prime readiness` : "Prime readiness", glyph: "✓", description: "What your company needs to hold a federal prime contract, and the data VETS needs from you to get it done." as string | undefined },
    ]) },
    { group: "Work with VETS", subtitle: "Proposals and opportunities for your company" as string | undefined, items: [
      { to: "/proposals/process", label: "Shipley Process", glyph: "⧗", description: "How a proposal moves from the first look at an opportunity to award." as string | undefined },
      { to: "/proposals/develop", label: "Your proposals", glyph: "✎", description: "Proposals VETS is writing for your company." as string | undefined },
      { to: "/opportunities", label: "Your opportunities", glyph: "◆", description: "Federal opportunities that fit your company." as string | undefined },
      { to: "/reports", label: "Your reports", glyph: "▧", description: "ArcLine reports about your company." as string | undefined },
      { to: "/glossary", label: "Glossary", glyph: "Aa", description: "Every acronym, spelled out." as string | undefined },
    ] },
  ];
  const fullNav = companyUser ? companyNav : [...pinnedNav, ...NAV];
  const filteredNav = navQuery.trim()
    ? fullNav.map((grp) => ({
        ...grp,
        items: grp.items.filter((it) => {
          const q = navQuery.trim().toLowerCase();
          return it.label.toLowerCase().includes(q) || (it.description?.toLowerCase().includes(q) ?? false);
        }),
      })).filter((grp) => grp.items.length > 0)
    : fullNav;

  const roleLabel = me?.superAdmin ? "VETS · Super admin" : companyUser ? `Company · ${titleCaseRole(me?.role ?? "")}` : `R3x · ${titleCaseRole(me?.role ?? "")}`;
  const asideStyle = phone
    ? { position: "fixed", top: 0, bottom: 0, left: 0, zIndex: 90, width: "min(300px, 86vw)", transform: menuOpen ? "none" : "translateX(-105%)", visibility: menuOpen ? "visible" : "hidden", transition: "transform 180ms ease, visibility 180ms", boxShadow: menuOpen ? "12px 0 40px rgba(0,0,0,0.35)" : "none" } as const
    : { width: 280, flex: "none" } as const;

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      {phone && menuOpen && <div onClick={() => setMenuOpen(false)} aria-hidden style={{ position: "fixed", inset: 0, background: "rgba(3,5,11,0.55)", zIndex: 89 }} />}
      {/* sidebar (a slide-out menu on phones) */}
      <aside id="arc-menu" aria-label="Main menu" style={{ ...asideStyle, background: c.side.bg, borderRight: "none", display: "flex", flexDirection: "column", padding: "18px 14px" }}>
        {/* Brand lockup — R3x platform + the R3x ethos line. */}
        <div style={{ padding: "0 6px 16px", marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/brand/r3x-white.png" alt="R3x" style={{ height: 22, width: "auto", objectFit: "contain" }} />
            <div style={{ width: 1, height: 20, background: c.side.line }} />
            <div style={{ font: `700 18px ${font.display}`, letterSpacing: "0.01em", color: "#FFFFFF", lineHeight: 1 }}>ArcLine</div>
            {phone && <button type="button" onClick={() => setMenuOpen(false)} aria-label="Close menu" style={{ marginLeft: "auto", background: "none", border: "none", color: "#FFFFFF", fontSize: 22, lineHeight: 1, padding: 6, cursor: "pointer" }}>✕</button>}
          </div>
          <div style={{ font: `500 12.5px ${font.sans}`, letterSpacing: "0.02em", color: c.side.muted, marginTop: 9 }}>
            Resilience. Readiness. Resolve.
          </div>
        </div>
        {phone && !companyUser && <div className="arc-menu-roster" style={{ marginBottom: 12 }}><RosterSwitch /></div>}
        <input value={navQuery} onChange={(e) => setNavQuery(e.target.value)} placeholder="Filter navigation…"
          style={{ background: "#FFFFFF", border: "none", borderRadius: 6, padding: "9px 13px", color: c.textHi, fontSize: phone ? 16 : 13.5, outline: "none", marginBottom: 12, boxSizing: "border-box" }} />
        <nav style={{ flex: 1, overflowY: "auto" }}>
          {filteredNav.length === 0 && <div style={{ fontSize: 13.5, color: c.side.muted, padding: "0 8px" }}>No pages match “{navQuery.trim()}”.</div>}
          {!navQuery.trim() && (
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "0 8px 6px" }}>
              <button onClick={() => setAll(!allCollapsed)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: `500 12.5px ${font.sans}`, color: c.side.muted, textDecoration: "underline" }}>
                {allCollapsed ? "Expand all" : "Collapse all"}
              </button>
            </div>
          )}
          {filteredNav.map((grp) => {
            const isOpen = navQuery.trim() !== "" || !collapsed[grp.group];
            const hasActive = grp.items.some((it) => it.to === loc.pathname);
            return (
            <div key={grp.group} style={{ marginBottom: isOpen ? 14 : 6 }}>
              <button type="button" onClick={() => toggleGroup(grp.group)} aria-expanded={isOpen}
                title={isOpen ? `Collapse ${grp.group}` : `Expand ${grp.group}`}
                style={{ display: "flex", alignItems: "flex-start", gap: 6, width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer", padding: "4px 8px 6px", borderRadius: 8 }}>
                <span style={{ width: 10, flex: "none", color: c.side.muted, fontSize: 11, lineHeight: "14px", transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 120ms" }}>▶</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", textTransform: "uppercase", color: hasActive && !isOpen ? "#FFFFFF" : c.side.muted }}>{grp.group}</span>
                    {!isOpen && <span style={{ font: `500 12px ${font.sans}`, color: c.side.hint }}>{grp.items.length}</span>}
                  </span>
                  {isOpen && grp.subtitle && <span style={{ display: "block", font: `400 12.5px ${font.sans}`, color: c.side.hint, marginTop: 2, lineHeight: 1.35 }}>{grp.subtitle}</span>}
                </span>
              </button>
              {isOpen && grp.items.map((it) => {
                // Active on the page itself and its sub-pages (an opportunity
                // record lights up Opportunities); pinned company links carry a
                // query string, so those compare path and query together.
                const active = it.to.includes("?") ? `${loc.pathname}${loc.search}` === it.to
                  : loc.pathname === it.to || (it.to.length > 1 && loc.pathname.startsWith(`${it.to}/`) && !NAV_PATHS.has(loc.pathname));
                const badge = it.to === "/pipeline" && pipelineCount != null ? pipelineCount : null;
                const need = needs[it.to];
                const content = (
                  <>
                    <span style={{ width: 18, color: active ? "#FFFFFF" : c.side.muted, fontSize: 15 }}>{it.glyph}</span>
                    <span style={{ flex: 1, fontSize: 14.5, fontWeight: active ? 700 : 500, color: active ? "#FFFFFF" : c.side.text }}>{it.label}</span>
                    {need && (
                      <span title={need} aria-label={`needs input: ${need}`} style={{ font: `700 12px ${font.sans}`, color: "#FFFFFF", background: c.soft.amber, borderRadius: 999, width: 16, height: 16, display: "grid", placeItems: "center", flex: "none" }}>!</span>
                    )}
                    {badge != null && (
                      <span style={{ ...tnum, font: `600 12px ${font.mono}`, color: c.side.bg, background: "#FFFFFF", borderRadius: 999, padding: "2px 8px", minWidth: 14, textAlign: "center" }}>{badge}</span>
                    )}
                  </>
                );
                const itemStyle = { display: "flex", alignItems: "center", gap: 9, padding: phone ? "11px 11px" : "9px 11px", borderRadius: 8, marginBottom: 3, cursor: "pointer", background: active ? c.side.active : "transparent" } as const;
                return (
                  <NavLink key={it.to} to={it.to} title={need ?? it.description ?? undefined} style={itemStyle}>
                    {content}
                  </NavLink>
                );
              })}
            </div>
            );
          })}
        </nav>
      </aside>

      {/* main */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {phone ? (
          <header style={{ minHeight: 56, flex: "none", display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: c.panel, borderBottom: `1px solid ${c.line}` }}>
            <button type="button" onClick={() => setMenuOpen(true)} aria-label="Open menu" aria-controls="arc-menu" aria-expanded={menuOpen}
              style={{ width: 40, height: 40, flex: "none", display: "grid", placeItems: "center", background: c.side.bg, color: "#FFFFFF", border: "none", borderRadius: 8, fontSize: 20, cursor: "pointer" }}>☰</button>
            <span className="arc-wordmark" style={{ font: `700 17px ${font.display}`, color: c.textHi, whiteSpace: "nowrap" }}>ArcLine</span>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
              {alertCount > 0 && (
                <button type="button" onClick={() => navigate("/actions")} aria-label={`${alertCount} urgent action items`}
                  style={{ display: "flex", alignItems: "center", gap: 4, font: `600 13px ${font.sans}`, color: c.soft.amber, background: withAlpha(c.soft.amber, 0.14), border: "none", borderRadius: 6, padding: "9px 9px", cursor: "pointer" }}>
                  <span>⚠</span><span>{alertCount}</span>
                </button>
              )}
              {!companyUser && <CommandPalette />}
              <ViewHelp path={loc.pathname} />
              <GuidePanel />
              <AccountMenu name={me?.fullName ?? ""} roleLabel={roleLabel} roleTint={roleColor(me?.role ?? "")} onTour={companyUser ? undefined : () => setShowTour(true)} onLogout={logout} />
            </div>
          </header>
        ) : (
        <header style={{ minHeight: 58, flex: "none", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "8px 24px", background: c.panel, borderBottom: `1px solid ${c.line}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <SessionClock />
            {!companyUser && <CommandPalette />}
            {!companyUser && <RosterSwitch />}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {alertCount > 0 && (
              <span onClick={() => navigate("/actions")} title={`${alertCount} outstanding critical/high-severity action item${alertCount === 1 ? "" : "s"}`}
                style={{ display: "flex", alignItems: "center", gap: 6, font: `600 13px ${font.sans}`, color: c.soft.amber, background: withAlpha(c.soft.amber, 0.14), border: "none", borderRadius: 6, padding: "7px 12px", cursor: "pointer" }}>
                <span>⚠</span><span>{alertCount}</span>
              </span>
            )}
            <ViewHelp path={loc.pathname} />
            <GuidePanel />
            {!companyUser && <button onClick={() => setShowTour(true)} title="Replay the welcome tour" style={pillBtn}>Tour</button>}
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: c.textHi, whiteSpace: "nowrap" }}>{me?.fullName}</div>
              <div style={{ font: `500 12.5px ${font.sans}`, letterSpacing: "0.04em", color: roleColor(me?.role ?? "") }}>
                {roleLabel}
              </div>
            </div>
            <button onClick={logout} style={pillBtn}>Sign out</button>
          </div>
        </header>
        )}
        {!companyUser && <RosterBanner />}
        <main className="arc-main" style={{ flex: 1, overflowY: "auto", padding: phone ? "14px 12px 88px" : "24px 28px" }}>
          <ErrorBoundary key={loc.pathname}>{children}</ErrorBoundary>
          {!companyUser && <FieldsDock />}
        </main>
      </div>
      {!companyUser && <Copilot />}
      {showTour && <WelcomeTour onDone={finishTour} />}
    </div>
  );
}
